import * as THREE from 'three';
import PickHelper from './PickHelper';
import MeshPickHelper from './MeshPickHelper';
import { ViewerLayer } from './ViewerLayer';

export class HybridRaycaster extends THREE.Raycaster {
  pickHelper?: PickHelper;
  meshPickHelper?: MeshPickHelper;

  pointerCoords: THREE.Vector2 | undefined;

  hadPicking: boolean;
  pickResults: THREE.Intersection[];

  renderer: THREE.WebGLRenderer | undefined;

  pickCanvasAreaPercent = 5;

  constructor() {
    super(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 1));
    this.pointerCoords = undefined;
    this.hadPicking = false;
    this.pickResults = [];
    this.params.Line.threshold = 0.05;
  }

  setPickHelper(pickHelper: PickHelper) {
    this.pickHelper = pickHelper;
  }
  setMeshPickHelper(meshPickHelper: MeshPickHelper) {
    this.meshPickHelper = meshPickHelper;
  }

  setRenderer(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
  }

  setPickCanvasAreaPercent(value: number) {
    this.pickCanvasAreaPercent = value;
  }

  set(origin: THREE.Vector3, direction: THREE.Vector3) {
    // direction is assumed to be normalized (for accurate distance calculations)
    super.set(origin, direction);
    this.pointerCoords = undefined;
    this.hadPicking = false;
  }

  setFromCamera(coords: THREE.Vector2, camera: THREE.Camera) {
    super.setFromCamera(coords, camera);
    this.pointerCoords = coords;
    this.hadPicking = false;
  }

  setFromXRController(controller: THREE.XRTargetRaySpace) {
    super.setFromXRController(controller);
    this.pointerCoords = undefined;
    this.hadPicking = false;
    return this;
  }

  pickObjects() {
    if (this.hadPicking) return;
    this.hadPicking = true;
    this.pickResults = [];
    const { camera, renderer, pointerCoords, pickHelper, meshPickHelper, pickCanvasAreaPercent } =
      this;
    if (!renderer) return;
    if (!pointerCoords) return;
    if (!meshPickHelper) return;
    if (!pickHelper) return;

    const canvas = renderer.domElement;
    const centerX = ((pointerCoords.x + 1) * canvas.width) / 2;
    const centerY = ((-pointerCoords.y + 1) * canvas.height) / 2;

    const result1 = meshPickHelper.pickObject(centerX, centerY, camera, renderer, undefined);
    if (result1) {
      this.pickResults.push(result1);
    }

    // hardcoded event priority here
    // instanced mesh has higher priority (to ensure image anchors under point clouds are clickable)
    if (!result1) {
      const result0 = pickHelper.pickObject(
        centerX,
        centerY,
        camera,
        renderer,
        pickCanvasAreaPercent
      );
      if (result0) {
        this.pickResults.push(result0);
      }
    }
  }

  intersectObject<TIntersected extends THREE.Object3D>(
    object: THREE.Object3D,
    recursive = true,
    intersects: Array<THREE.Intersection<TIntersected>> = []
  ): Array<THREE.Intersection<TIntersected>> {
    if (recursive) {
      this.pickObjects();
      intersects.push(...(this.pickResults as Array<THREE.Intersection<TIntersected>>));
    }
    intersect(object, this, intersects, recursive);

    intersects.sort(ascSort);

    return intersects;
  }

  intersectObjects<TIntersected extends THREE.Object3D>(
    objects: THREE.Object3D[],
    recursive = true,
    intersects: Array<THREE.Intersection<TIntersected>> = []
  ): Array<THREE.Intersection<TIntersected>> {
    for (let index = 0, l = objects.length; index < l; index++) {
      intersect(objects[index], this, intersects, recursive);
    }

    intersects.sort(ascSort);

    return intersects;
  }
}

function ascSort<TIntersected extends THREE.Object3D>(
  a: THREE.Intersection<TIntersected>,
  b: THREE.Intersection<TIntersected>
) {
  return a.distance - b.distance;
}

function intersect<TIntersected extends THREE.Object3D>(
  object: THREE.Object3D,
  raycaster: HybridRaycaster,
  intersects: Array<THREE.Intersection<TIntersected>>,
  recursive: boolean
) {
  if (object.layers.isEnabled(ViewerLayer.AutoPickable) && object.layers.test(raycaster.layers)) {
    object.raycast(raycaster, intersects);
  }

  if (recursive === true) {
    const children = object.children;

    for (let index = 0, l = children.length; index < l; index++) {
      intersect(children[index], raycaster, intersects, true);
    }
  }
}
